import React, { useState, useEffect, useMemo } from 'react';
import { BranchIncomeRecord, ExpenseRecord } from '../../types/db';
import { Printer, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { fetchIncomeReport, fetchExpenseReport } from '../../services/reportService';
import { getReportPeriod } from '../../lib/reportPeriod';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = 10;
const YEARS = Array.from({ length: YEAR_RANGE }, (_, i) => CURRENT_YEAR - i);

const MONTHS = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

interface ReportsCenterProps {
  branchId: string;
  branchName: string;
}

export function ReportsCenter({ branchId, branchName }: ReportsCenterProps) {
  const [reportType, setReportType] = useState<'MONTHLY' | 'ANNUAL'>('MONTHLY');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

  const [reportIncomes, setReportIncomes] = useState<BranchIncomeRecord[]>([]);
  const [reportExpenses, setReportExpenses] = useState<ExpenseRecord[]>([]);
  const [loading, setLoading] = useState(false);

  // Mengambil periode display untuk UI
  const periodInfo = useMemo(() => {
    return getReportPeriod({ reportType, year: selectedYear, month: selectedMonth });
  }, [reportType, selectedYear, selectedMonth]);

  // Load data dari backend saat filter berubah
  useEffect(() => {
    const loadReportData = async () => {
      setLoading(true);
      try {
        const [inc, exp] = await Promise.all([
          fetchIncomeReport({ branchId, reportType, year: selectedYear, month: selectedMonth }),
          fetchExpenseReport({ branchId, reportType, year: selectedYear, month: selectedMonth }),
        ]);
        setReportIncomes(inc);
        setReportExpenses(exp);
      } catch (err: any) {
        /* console removed */
      } finally {
        setLoading(false);
      }
    };
    loadReportData();
  }, [branchId, reportType, selectedYear, selectedMonth]);

  const totals = useMemo(() => {
    const totalIn = reportIncomes.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    const totalOut = reportExpenses.reduce((a, b) => a + (Number(b.amount) || 0), 0);
    return { totalIn, totalOut, profit: totalIn - totalOut };
  }, [reportIncomes, reportExpenses]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handlePrint = () => {
    const content = window.document.getElementById('report-to-print');
    if (!content) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Laporan Keuangan - ${branchName}</title>
          <script src="https://cdn.tailwindcss.com"></script>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap" rel="stylesheet">
          <style>
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #1e293b; }
            @media print { .no-print { display: none; } }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; font-size: 12px; }
            th { background-color: #f8fafc; font-weight: 800; text-transform: uppercase; }
          </style>
        </head>
        <body>
          <div class="flex justify-between items-start border-b-4 border-indigo-600 pb-6 mb-8">
            <div>
              <h1 class="text-3xl font-black uppercase tracking-tighter">Bimbel DELTA</h1>
              <p class="text-slate-400 font-bold uppercase tracking-widest text-[9px]">Official Academic & Finance Report</p>
            </div>
            <div class="text-right">
              <h2 class="font-black text-indigo-600 uppercase text-lg">${branchName}</h2>
              <p class="text-xs font-bold text-slate-500">${periodInfo.displayStart} - ${periodInfo.displayEnd}</p>
            </div>
          </div>
          ${content.innerHTML}
          <div class="mt-20 flex justify-between">
            <div class="text-center w-48"><p class="text-xs font-bold mb-16">Finance Admin,</p><div class="border-t-2 border-slate-900 w-full pt-2 text-[10px] font-black uppercase">( ________________ )</div></div>
            <div class="text-center w-48"><p class="text-xs font-bold mb-16">Branch Manager,</p><div class="border-t-2 border-slate-900 w-full pt-2 text-[10px] font-black uppercase">( ________________ )</div></div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    window.setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <div className="space-y-8 animate-fade-in font-sans">
      <div className="bg-white p-8 rounded-[3rem] border border-gray-100 shadow-sm relative overflow-hidden">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-50 flex items-center justify-center">
            <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
          </div>
        )}

        <div className="flex flex-col lg:flex-row justify-between items-end gap-6 mb-10 border-b border-slate-50 pb-10">
          <div className="space-y-6 w-full lg:w-auto">
            <div className="flex bg-slate-50 p-1 rounded-2xl w-fit">
              <button
                onClick={() => setReportType('MONTHLY')}
                className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'MONTHLY' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
              >
                Bulanan
              </button>
              <button
                onClick={() => setReportType('ANNUAL')}
                className={`px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${reportType === 'ANNUAL' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-600'}`}
              >
                Tahunan (T.A)
              </button>
            </div>

            <div className="flex flex-wrap gap-4">
              {reportType === 'MONTHLY' && (
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-2">
                    Bulan
                  </label>
                  <select
                    className="border-2 border-slate-50 p-4 rounded-2xl text-[10px] font-black uppercase bg-slate-50/50 text-indigo-700 outline-none w-48"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-2">
                  {reportType === 'ANNUAL' ? 'Tahun Ajaran' : 'Tahun'}
                </label>
                <select
                  className="border-2 border-slate-50 p-4 rounded-2xl text-[10px] font-black uppercase bg-slate-50/50 text-indigo-700 outline-none w-40"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {reportType === 'ANNUAL' ? `${y - 1} / ${y}` : y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="w-full lg:w-auto bg-slate-900 text-white px-10 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-3 hover:bg-black transition-all"
          >
            <Printer className="w-5 h-5" /> CETAK LAPORAN RESMI
          </button>
        </div>

        <div id="report-to-print">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="p-8 bg-emerald-50 rounded-[2.5rem] border border-emerald-100">
              <p className="text-[9px] font-black text-emerald-600 uppercase mb-2">
                Total Pendapatan
              </p>
              <p className="text-2xl font-black text-emerald-900">
                Rp {totals.totalIn.toLocaleString()}
              </p>
            </div>
            <div className="p-8 bg-rose-50 rounded-[2.5rem] border border-rose-100">
              <p className="text-[9px] font-black text-rose-600 uppercase mb-2">
                Total Pengeluaran
              </p>
              <p className="text-2xl font-black text-rose-900">
                Rp {totals.totalOut.toLocaleString()}
              </p>
            </div>
            <div
              className={`p-8 rounded-[2.5rem] text-white shadow-xl transition-colors ${totals.profit >= 0 ? 'bg-indigo-900' : 'bg-rose-900'}`}
            >
              <p className="text-[9px] font-black text-indigo-300 uppercase mb-2">
                Laba / Rugi Bersih
              </p>
              <p className="text-2xl font-black">Rp {totals.profit.toLocaleString()}</p>
            </div>
          </div>

          <div className="space-y-10">
            <div>
              <h4 className="font-black text-indigo-900 uppercase text-xs tracking-widest mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Rincian Pemasukan
              </h4>
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Keterangan</th>
                    <th className="text-right">Nominal</th>
                  </tr>
                </thead>
                <tbody>
                  {reportIncomes.map((i, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(i.date)}</td>
                      <td>{i.description}</td>
                      <td className="text-right font-bold">Rp {i.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  {reportIncomes.length === 0 && !loading && (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-slate-300 italic">
                        Tidak ada data pemasukan pada periode ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h4 className="font-black text-rose-600 uppercase text-xs tracking-widest mb-4 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" /> Rincian Pengeluaran
              </h4>
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Keterangan</th>
                    <th>Kategori</th>
                    <th className="text-right">Nominal</th>
                  </tr>
                </thead>
                <tbody>
                  {reportExpenses.map((e, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(e.date)}</td>
                      <td>{e.description}</td>
                      <td>{e.category}</td>
                      <td className="text-right font-bold">Rp {e.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  {reportExpenses.length === 0 && !loading && (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-slate-300 italic">
                        Tidak ada data pengeluaran pada periode ini.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
